import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import DatePickerField from "components/lib/Shared/DatePickerField";
import { format, subDays } from "date-fns";
import { Field } from "formik";
import { useEffect } from "react";
import { useSelector } from "react-redux";

const AnnualReturns = ({
  field,
  form: {
    values,
    errors,
    touched,
    setFieldValue,
    validateField,
    setFieldTouched,
    isSubmitting,
  },

  transformedComments,
  yearOptions,
}) => {
  const { companyFormation, company } = useSelector(
    (state) => state.companySlice
  );

  const documentData = [
    {
      label: "Annual Returns",
      name: "annualReturns",
      accept: "application/pdf",
    },
  ];

  const annualReturns = company?.annualReturns;

  // Filter years to render based on selected lastAnnualReturnYear
  const selectedYear = values.annualReturns?.lastAnnualReturnYear;
  const filteredYearOptions = yearOptions.filter((option) =>
    annualReturns?.length > 0
      ? parseInt(option.value.split("/")[0]) > parseInt(selectedYear)
      : parseInt(option.value.split("/")[0]) >= parseInt(selectedYear)
  );
  // Set touched for lastAnnualReturnYear and fileUploads when values change
  useEffect(() => {
    if (values.annualReturns?.lastAnnualReturnYear) {
      setFieldTouched("annualReturns.lastAnnualReturnYear", true);
    }
    // Set touched for fileUploads when a file exists
    values.annualReturns?.returns?.forEach((_, index) => {
      const fileUploadsName = `annualReturns.returns[${index}].fileUploads`;
      const fileUploads = values.annualReturns?.returns[index]?.fileUploads;
      if (fileUploads?.annualReturns) {
        setFieldTouched(fileUploadsName, true);
        validateField(fileUploadsName);
      }
    });
  }, [
    values.annualReturns?.lastAnnualReturnYear,
    values.annualReturns?.returns,
    setFieldTouched,
    validateField,
  ]);

  return (
    <div>
      <div className="flex items-baseline justify-between m-4">
        <label
          htmlFor={field.name}
          className="block mb-2 text-sm text-primary font-bold min-w-[13rem] w-52 whitespace-normal"
        >
          {`Year of ${
            annualReturns?.length > 0 ? "last" : "first"
          } annual return`}
        </label>
        <div
          className={`bg-formInput flex w-[80%] justify-between relative p-2`}
        >
          {values.annualReturns?.lastAnnualReturnYear}
        </div>
      </div>
      {filteredYearOptions.map((option, index) => {
        if (!values?.annualReturns?.returns?.[index]) {
          return <div>Loading...</div>; // or loading state
        }

        const dateName = `annualReturns.returns[${index}].dateOfReturn`;
        const fileUploadsName = `annualReturns.returns[${index}].fileUploads`;

        const [yearStr, monthStr, dayStr] = option.value.split("/");
        const centralDate = new Date(`${yearStr}-${monthStr}-${dayStr}`);
        const isValidCentralDate = !isNaN(centralDate.getTime());
        // Calculate minDate  (-28 days)
        const minDate = isValidCentralDate ? subDays(centralDate, 28) : null;
        const maxDate = isValidCentralDate ? centralDate : null;

        return (
          <div
            key={option.value}
            className="shadow-sm sm:rounded-md sm:overflow-hidden px-4 py-6 space-y-4 bg-gray-50 sm:p-6 mt-4"
          >
            <div className=" mb-4">
              <h2 className="text-4xl font-bold text-gray-800">
                {option.label.split("/")[0]}
              </h2>
              <p className="text-gray-800 font-bold">Annual Return</p>
            </div>
            <div className="mt-4">
              <DatePickerField
                label="Date of this Return"
                onChange={(date) => {
                  if (!date) {
                    setFieldValue(dateName, "");
                    return;
                  }
                  if (isNaN(date.getTime())) {
                    console.error("Invalid date:", date);
                    return;
                  }
                  const formattedDate = format(date, "yyyy/MM/dd");
                  setFieldValue(dateName, formattedDate);
                  setFieldTouched(dateName, true);
                  validateField(dateName);
                }}
                selected={
                  values?.annualReturns?.returns[index]?.dateOfReturn
                    ? new Date(
                        values?.annualReturns?.returns[index].dateOfReturn
                      )
                    : null
                }
                name={dateName}
                placeholderText="Enter Return date"
                errorPath={dateName}
                maxDate={maxDate}
                minDate={minDate}
                disabled={isSubmitting}
                showRequiredAsterisk
              />
              <ValidationError
                errorMessage={
                  errors?.annualReturns?.returns?.[index]?.dateOfReturn
                }
                isTouched={
                  touched?.annualReturns?.returns?.[index]?.dateOfReturn
                }
              />
            </div>

            <Field
              name={fileUploadsName}
              component={FileUploadForm}
              documentData={documentData}
              setFieldValue={(name, value) => {
                setFieldValue(name, value);
                setFieldTouched(name, true);
                validateField(name);
              }}
              setFieldTouched={(name) => {
                setFieldTouched(name, true);
                validateField(name);
              }}
              showRequiredAsterisk
              showModeType={values?.annualReturns?.returns?.find(
                (el, indexValue) =>
                  indexValue === index && el?.fileUploads?.annualReturns
              )}
            />
            <ValidationError
              errorMessage={
                errors?.annualReturns?.returns?.[index]?.fileUploads
              }
              isTouched={touched?.annualReturns?.returns?.[index]?.fileUploads}
            />
          </div>
        );
      })}
    </div>
  );
};

export default AnnualReturns;

const ValidationError = ({ errorMessage, isTouched }) => {
  if (!errorMessage || !isTouched) return null;

  return (
    <>
      <div className="text-red-500 flex items-center py-2">
        <svg
          aria-hidden="true"
          className="flex-shrink-0 inline w-5 h-5 mr-1"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          ></path>
        </svg>

        <p className="text-sm">{errorMessage}</p>
      </div>
    </>
  );
};
